/**
 * imagePrep — client-side photo prep before a gallery upload.
 *
 * GAL-7. Two jobs, one decode:
 *
 *   1. DIMENSIONS — read the true (orientation-applied) pixel size
 *      so `gallery_photos.width/height` can be stored at insert
 *      time. The justified grid then lays out with the real aspect
 *      ratio on first paint instead of guessing 3:2 and reflowing
 *      as images load (the "squish").
 *
 *   2. RECOMPRESSION — Pixieset uploads feel like "seconds" partly
 *      because the browser never ships the raw 20–40 MB camera
 *      JPEG. We downscale anything over DELIVERY_LONG_EDGE to that
 *      long edge and re-encode as JPEG. A 24 MB 8000px frame
 *      becomes ~2–3 MB at 4096px — a 10× cut in bytes on the wire,
 *      still >13" at 300 DPI for prints, and comfortably above the
 *      2400px hero rendition the client gallery ever displays.
 *
 * Deliberate trade-off, called out loudly: the ORIGINAL bytes are
 * not kept. Client galleries are a delivery surface (view, pick,
 * download finals) — 4096px JPEG q0.85 is delivery-grade. If a
 * "store originals" tier ships later, gate the recompression on it.
 *
 * Safety rails:
 *   • Decode/encode failures NEVER block an upload — we fall back
 *     to the untouched file with null dims (grid reflows like
 *     before, bytes still land).
 *   • Small files (≤ RECOMPRESS_BYTES) skip re-encoding entirely —
 *     already-optimized exports shouldn't be double-compressed —
 *     but still get their dimensions read.
 *   • Non-image files pass straight through.
 *   • Uses OffscreenCanvas when available (off-main-thread encode),
 *     falls back to a regular canvas otherwise.
 */

/** Long edge of the delivered JPEG. */
export const DELIVERY_LONG_EDGE = 4096;
/** Files at or under this size are stored as-is (dims still read). */
export const RECOMPRESS_BYTES = 4 * 1024 * 1024;
/** JPEG encode quality for recompressed frames. */
export const DELIVERY_QUALITY = 0.85;

export interface PreppedImage {
  /** The file to upload — recompressed JPEG or the untouched input. */
  file: File;
  /** True oriented pixel size of `file`, or null when decode failed. */
  width: number | null;
  height: number | null;
  /** True when the bytes were downscaled/re-encoded. */
  recompressed: boolean;
}

/**
 * Decode a blob to something drawable, honoring EXIF orientation.
 * `createImageBitmap` applies orientation by default in every
 * browser that ships OffscreenCanvas; the <img> fallback relies on
 * the (also default) `image-orientation: from-image` behavior.
 */
async function decode(
  blob: Blob,
): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap === 'function') {
    const bmp = await createImageBitmap(blob);
    return {
      source: bmp,
      width: bmp.width,
      height: bmp.height,
      close: () => bmp.close?.(),
    };
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image decode failed'));
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

/** Encode a drawn canvas to a JPEG blob, OffscreenCanvas-first. */
async function encodeJpeg(
  source: CanvasImageSource,
  outW: number,
  outH: number,
): Promise<Blob> {
  if (typeof OffscreenCanvas === 'function') {
    const canvas = new OffscreenCanvas(outW, outH);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    ctx.drawImage(source, 0, 0, outW, outH);
    return canvas.convertToBlob({ type: 'image/jpeg', quality: DELIVERY_QUALITY });
  }
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  ctx.drawImage(source, 0, 0, outW, outH);
  try {
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
        'image/jpeg',
        DELIVERY_QUALITY,
      );
    });
  } finally {
    // Release the backing store promptly — a 40-photo batch would
    // otherwise pin GBs until GC catches up.
    canvas.width = 1;
    canvas.height = 1;
  }
}

/**
 * Prep one image for upload. Never throws — the worst case is the
 * untouched file with null dimensions.
 */
export async function prepImageForUpload(input: File): Promise<PreppedImage> {
  if (!input.type.startsWith('image/')) {
    return { file: input, width: null, height: null, recompressed: false };
  }
  let decoded: Awaited<ReturnType<typeof decode>> | null = null;
  try {
    decoded = await decode(input);
    const { width, height } = decoded;
    const longEdge = Math.max(width, height);
    const needsResize = longEdge > DELIVERY_LONG_EDGE;
    const needsRecompress = needsResize || input.size > RECOMPRESS_BYTES;
    // GIFs stay untouched (animation would be flattened); SVG never
    // reaches here (type filter upstream accepts raster drops only,
    // and decode of huge SVGs is unreliable anyway).
    if (!needsRecompress || input.type === 'image/gif') {
      return { file: input, width, height, recompressed: false };
    }
    const scale = needsResize ? DELIVERY_LONG_EDGE / longEdge : 1;
    const outW = Math.max(1, Math.round(width * scale));
    const outH = Math.max(1, Math.round(height * scale));
    const blob = await encodeJpeg(decoded.source, outW, outH);
    // Re-encoding an already-lean file can come out BIGGER (e.g. a
    // well-optimized 5 MB export at q0.85) — keep whichever is
    // smaller so we never inflate the upload.
    if (!needsResize && blob.size >= input.size) {
      return { file: input, width, height, recompressed: false };
    }
    const outName = input.name.replace(/\.(png|webp|tiff?|bmp|heic|heif)$/i, '.jpg');
    const file = new File(
      [blob],
      /\.(jpe?g)$/i.test(outName) ? outName : `${outName}.jpg`,
      { type: 'image/jpeg' },
    );
    return { file, width: outW, height: outH, recompressed: true };
  } catch {
    // Decode failed (exotic format, corrupt frame, canvas OOM) —
    // upload the original bytes; the grid measures on load like it
    // always did. Deliberately quiet: this is a per-file downgrade,
    // not a failure the studio can act on.
    return { file: input, width: null, height: null, recompressed: false };
  } finally {
    decoded?.close();
  }
}

/**
 * Prep a batch with bounded concurrency (decode + encode are CPU
 * work — more than ~3 in flight just thrashes the main thread).
 * Order of the result matches the input order.
 */
export async function prepImagesForUpload(
  files: File[],
  concurrency = 3,
  onPrepped?: (index: number, result: PreppedImage) => void,
): Promise<PreppedImage[]> {
  const results: PreppedImage[] = new Array(files.length);
  let cursor = 0;
  async function worker() {
    while (true) {
      const idx = cursor++;
      if (idx >= files.length) return;
      const result = await prepImageForUpload(files[idx]);
      results[idx] = result;
      onPrepped?.(idx, result);
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(concurrency, files.length) }, () => worker()),
  );
  return results;
}
